<script setup lang="ts">
import { NButton, NModal, useMessage } from 'naive-ui'
import { onMounted, ref, watch } from 'vue'
import mammoth from 'mammoth'
import axios from 'axios'
import { downloadFileToLocal } from '@/utils/functions/file'
import { t } from '@/locales'
import { AppSpin } from '@/components/common'

const props = defineProps({
  fileId: {
    type: String,
    required: false,
  },
  filePath: {
    type: String,
    required: false,
  },
  sourceOrigin: {
    type: String,
    required: false,
  },
  isShowPreviewFile: {
    type: Boolean,
    required: true,
  },
})

const emit = defineEmits(['handleClose'])

const message = useMessage()

const showPreviewModal = ref<boolean>(props.isShowPreviewFile)
const previewFileData = ref<any>(null)
const fileType = ref<string | null>(null)
const loading = ref<boolean>(false)

const getDataFile = async () => {
  try {
    loading.value = true
    const params: any = {}
    if (props.fileId)
      params.file_id = props.fileId
    else if (props.filePath)
      params.file_path = props.filePath
    else if (props.sourceOrigin)
      params.source_origin = props.sourceOrigin

    // Thêm responseType: 'blob' để nhận binary data đúng cách
    const res = await axios.get(params.file_path, { responseType: 'blob' })

    if (res && res.data) {
      const blob = res.data

      // Lấy file type từ Content-Type header hoặc từ blob type
      let detectedFileType = res.headers['content-type'] || blob.type || 'application/octet-stream'

      // Nếu vẫn là octet-stream, thử detect từ file extension
      if (detectedFileType === 'application/octet-stream' && params.file_path) {
        const extension = params.file_path?.split('.').pop()?.toLowerCase()
        if (extension === 'pdf')
          detectedFileType = 'application/pdf'

        else if (extension === 'docx')
          detectedFileType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

        else if (extension === 'txt')
          detectedFileType = 'text/plain'
      }

      fileType.value = detectedFileType
      const downloadUrl = URL.createObjectURL(blob)

      if (detectedFileType === 'application/pdf') {
        previewFileData.value = downloadUrl
      }
      else if (detectedFileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const arrayBuffer = await blob.arrayBuffer()
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer })
          previewFileData.value = result.value
        }
        catch (err) {
          previewFileData.value = null
          message.error('Lỗi khi xem trước file docx.')
        }
      }
      else if (detectedFileType === 'text/plain') {
        previewFileData.value = await blob.text()
      }
      else {
        previewFileData.value = null
        message.error('Định dạng file này chưa hỗ trợ xem trước. Vui lòng tải về để xem.')
      }
    }
    else {
      message.error('Đã có lỗi xảy ra')
    }
  }
  catch {
    message.error('Đã có lỗi xảy ra')
  }
  finally {
    loading.value = false
  }
}

const handleCloseModal = () => {
  showPreviewModal.value = false
  emit('handleClose')
}

const handleDownload = async () => {
  try {
    if (props.sourceOrigin)
      await downloadFileToLocal({ source_origin: props.sourceOrigin }, '')
    message.success(t('common.downloadSuccess'))
  }
  catch (err) {
    message.error(t('common.downloadFailed'))
  }
}

// Watch for modal open to load file
watch(() => props.isShowPreviewFile, (isOpen) => {
  if (isOpen && (props.fileId || props.filePath || props.sourceOrigin)) {
    // Reset previous data
    previewFileData.value = null
    fileType.value = null
    getDataFile()
  }
  showPreviewModal.value = isOpen
})

onMounted(async () => {
  if (props.isShowPreviewFile && (props.fileId || props.filePath || props.sourceOrigin))
    await getDataFile()
})
</script>

<template>
  <NModal
    v-model:show="showPreviewModal"
    preset="card"
    :title="$t('upload.previewDocument')"
    style="width: 1400px; min-height: 95vh"
    class="!rounded-2xl preview-modal !max-h-[95vh]"
    :on-after-leave="handleCloseModal"
  >
    <div class="preview-container">
      <AppSpin v-if="loading" :show="loading">
        <div class="h-full" style="min-height: calc(100vh - 145px)" />
      </AppSpin>
      <!-- <div v-else class="preview-content">
        <embed
          :src="filePath"
          class="preview-embed"
        >
      </div> -->
      <div v-else class="preview-content">
        <embed
          v-if="previewFileData && fileType === 'application/pdf'"
          :src="previewFileData"
          :type="fileType"
          class="preview-embed"
        >
        <div v-else-if="previewFileData && fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'" class="preview-html" v-html="previewFileData" />
        <div v-else-if="previewFileData && fileType === 'text/plain'" class="preview-html" style="white-space: pre-wrap" v-text="previewFileData" />
        <div v-else class="flex items-center justify-center h-full">
          <div class="mr-1">
            {{ $t('common.unsupported_format') }}
          </div>
          <NButton text class="mt-4" type="primary" @click="handleDownload">
            {{ $t('common.download') }}
          </NButton>
        </div>
      </div>
    </div>
    <!-- <template #footer>
      <div class="flex justify-end gap-2">
        <NButton @click="handleCloseModal">
          {{ $t('common.cancel') }}
        </NButton>
      </div>
    </template> -->
  </NModal>
</template>

<style scoped>
.preview-modal {
  display: flex;
  flex-direction: column;
}

.preview-container {
  width: 100%;
  min-height: calc(100vh - 145px);
  /* min-height: 80vh; */
  max-height: 90vh;
  overflow: auto;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-content {
  width: 100%;
  /* min-height: 60vh; */
  /* max-height: 80vh; */
  min-height: calc(100vh - 145px);
  background: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: auto;
  height: 100%;
}

.preview-embed {
  width: 100%;
  height: 80vh;
  min-height: 60vh;
  max-height: 80vh;
  border: none;
  display: block;
}

.preview-html {
  width: 100%;
  min-height: 60vh;
  max-height: 80vh;
  overflow: auto;
  display: block;
}
</style>
